import { defaultRangeExtractor, Range, Virtualizer } from "@tanstack/react-virtual";
import { RefObject, useCallback, useMemo, useRef } from "react";

import { calculateStickyRanges } from "ds/components/Table/hooks/useStickyRow/helpers";
import { TreeNode } from "utils/tree";
import { SpaceWithParentAccessLevel } from "views/Account/Spaces/types";

import { STICKY_TOP_BASE_PX } from "./constants";
import {
  buildStickyRanges,
  findFirstRowBelow,
  getMaxStickyLevel,
  stickyStackFitsContainer,
} from "./stickyHelpers";

type SpacesListNode = TreeNode<SpaceWithParentAccessLevel>;

const useSpacesListStickyRangeExtractor = (
  nodes: SpacesListNode[],
  tableRef: RefObject<HTMLDivElement | null>,
  virtualizerRef: RefObject<Virtualizer<HTMLDivElement, Element> | null>
) => {
  const stickyRangesIndexes = useMemo(() => buildStickyRanges(nodes), [nodes]);

  // Which groups are currently expanded. Kept separate from the pinned set so the tree guide
  // border stays correct even when a row stops being pinned.
  const openGroupStarts = useMemo(
    () => new Set(stickyRangesIndexes.map(([start]) => start)),
    [stickyRangesIndexes]
  );

  const stickyGroupsVisibleOnTableRef = useRef<Map<number, number>>(new Map());
  const forcedStickyGroupsRef = useRef<Map<number, number>>(new Map());

  const rangeExtractor = useCallback(
    (range: Range) => {
      const measurements = virtualizerRef.current?.measurementsCache ?? [];
      const scrollTop = tableRef.current?.scrollTop ?? 0;

      // First row visible *below the sticky header area* (`scrollTop + head height`), not below the
      // raw scroll top. A row peeking out only behind the head shouldn't keep its parents pinned:
      // if it does, those parents stay stuck while the eviction animation is already sliding them
      // away, and the disagreement shows up as a one-frame blink.
      const foldIndex =
        measurements.length > 0
          ? findFirstRowBelow(measurements, scrollTop + STICKY_TOP_BASE_PX)
          : range.startIndex;

      const defaultRange = defaultRangeExtractor(range);
      const { stickyGroups, newRange } = calculateStickyRanges(
        [foldIndex, range.endIndex],
        defaultRange,
        stickyRangesIndexes
      );

      const maxStickyLevel = getMaxStickyLevel(stickyGroups, foldIndex, nodes);
      const containerHeight = tableRef.current?.clientHeight ?? 0;

      // If the headers pinned at this point wouldn't fit the scroll container, drop pinning
      // altogether and scroll like a plain list — a stack that deep covers the rows it belongs to.
      if (!stickyStackFitsContainer(maxStickyLevel, containerHeight)) {
        stickyGroupsVisibleOnTableRef.current = new Map();
        forcedStickyGroupsRef.current = new Map();

        return defaultRange;
      }

      const defaultRangeStart = defaultRange[0] ?? 0;
      stickyGroupsVisibleOnTableRef.current = stickyGroups;
      forcedStickyGroupsRef.current = new Map(
        [...stickyGroups].filter(([groupIndex]) => groupIndex < defaultRangeStart)
      );

      return newRange;
    },
    [stickyRangesIndexes, nodes, tableRef, virtualizerRef]
  );

  return {
    rangeExtractor,
    stickyGroupsVisibleOnTableRef,
    forcedStickyGroupsRef,
    openGroupStarts,
  };
};

export default useSpacesListStickyRangeExtractor;
